import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { 
  HiChevronDown, 
  HiChevronRight, 
  HiCalendar, 
  HiFlag, 
  HiUserCircle,
  HiPlus,
  HiCheckCircle,
  HiClock
} from 'react-icons/hi'
import { tasksApi } from '../../api/client'
import CreateTaskModal from './CreateTaskModal'

const PRIORITY_CONFIG = {
  critical: { color: 'text-red-600 bg-red-50', label: 'Critical', dot: 'bg-red-500' },
  high: { color: 'text-orange-600 bg-orange-50', label: 'High', dot: 'bg-orange-500' },
  medium: { color: 'text-yellow-600 bg-yellow-50', label: 'Medium', dot: 'bg-yellow-500' },
  low: { color: 'text-green-600 bg-green-50', label: 'Low', dot: 'bg-green-500' },
}

const STATUS_ICONS = {
  todo: HiClock,
  'in-progress': HiChevronRight,
  done: HiCheckCircle,
}

export default function ListView({ board, onUpdate }) {
  const [columns, setColumns] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedGroups, setExpandedGroups] = useState({})
  const [showCreateTask, setShowCreateTask] = useState(null)

  const fetchBoardData = useCallback(async () => {
    if (!board?.id) return
    try {
      setLoading(true)
      const response = await tasksApi.getBoardTasks(board.id)
      // API returns { board, columns } - extract columns array
      const data = Array.isArray(response.data?.columns) ? response.data.columns : []
      const normalizedData = data.map(col => ({ ...col, tasks: Array.isArray(col.tasks) ? col.tasks : [] }))
      setColumns(normalizedData)
      // Expand all groups by default
      const expanded = {}
      normalizedData.forEach(col => {
        expanded[col.id] = true
      })
      setExpandedGroups(expanded)
    } catch (error) {
      console.error('Failed to fetch board data:', error)
    } finally {
      setLoading(false)
    }
  }, [board?.id])

  useEffect(() => {
    fetchBoardData()
  }, [fetchBoardData])

  const toggleGroup = (columnId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }))
  }

  const handleCreateTask = async (taskData) => {
    try {
      await tasksApi.create({
        ...taskData,
        project_id: board.project_id,
        board_id: board.id,
        column_id: showCreateTask,
      })
      setShowCreateTask(null)
      fetchBoardData()
      onUpdate?.()
    } catch (error) {
      console.error('Failed to create task:', error)
    }
  }

  const handleStatusChange = async (taskId, newColumnId) => {
    try {
      await tasksApi.update(taskId, { column_id: newColumnId })
      fetchBoardData()
    } catch (error) {
      console.error('Failed to update task:', error)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full"></div>
      </div>
    )
  }

  const totalTasks = columns.reduce((sum, col) => sum + col.tasks.length, 0)

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-4xl mx-auto py-4">
        {/* Summary Header */}
        <div className="flex items-center justify-between mb-4 px-2">
          <span className="text-sm text-surface-500">
            {totalTasks} task{totalTasks !== 1 ? 's' : ''} in {columns.length} groups
          </span>
        </div>

        {/* Grouped Tasks */}
        <div className="space-y-2">
          {columns.map(column => (
            <div key={column.id} className="bg-white rounded-xl border border-surface-200 overflow-hidden">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(column.id)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {expandedGroups[column.id] ? (
                    <HiChevronDown className="w-5 h-5 text-surface-400" />
                  ) : (
                    <HiChevronRight className="w-5 h-5 text-surface-400" />
                  )}
                  <span className="font-semibold text-surface-800">{column.name}</span>
                  <span className="px-2 py-0.5 text-xs font-medium bg-surface-100 text-surface-600 rounded-full">
                    {column.tasks.length}
                  </span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowCreateTask(column.id)
                  }}
                  className="p-1 text-surface-400 hover:text-brand-600 hover:bg-brand-50 rounded transition-colors"
                >
                  <HiPlus className="w-4 h-4" />
                </button>
              </button>

              {/* Tasks List */}
              {expandedGroups[column.id] && (
                <div className="border-t border-surface-100">
                  {column.tasks.length === 0 ? (
                    <div className="px-4 py-8 text-center text-surface-400 text-sm">
                      No tasks in this group
                    </div>
                  ) : (
                    <div className="divide-y divide-surface-100">
                      {column.tasks.map(task => (
                        <TaskListItem 
                          key={task.id} 
                          task={task} 
                          columns={columns}
                          onStatusChange={handleStatusChange}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Create Task Modal */}
      {showCreateTask && (
        <CreateTaskModal
          columnId={showCreateTask}
          onClose={() => setShowCreateTask(null)}
          onCreate={handleCreateTask}
        />
      )}
    </div>
  )
}

function TaskListItem({ task, columns, onStatusChange }) {
  const [showStatusMenu, setShowStatusMenu] = useState(false)
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'done'
  const priority = PRIORITY_CONFIG[task.priority]

  return (
    <div className="flex items-center gap-4 px-4 py-3 hover:bg-surface-50 transition-colors group">
      {/* Status Dropdown */}
      <div className="relative">
        <button
          onClick={() => setShowStatusMenu(!showStatusMenu)}
          className="w-5 h-5 rounded border-2 border-surface-300 hover:border-brand-500 transition-colors flex items-center justify-center"
        >
          {task.status === 'done' && (
            <HiCheckCircle className="w-5 h-5 text-green-500" />
          )}
        </button>
        
        {showStatusMenu && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setShowStatusMenu(false)} />
            <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-surface-200 rounded-lg shadow-lg py-1 min-w-[140px]">
              {columns.map(col => (
                <button
                  key={col.id}
                  onClick={() => {
                    onStatusChange(task.id, col.id)
                    setShowStatusMenu(false)
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-surface-50 transition-colors"
                >
                  {col.name}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Task Info */}
      <div className="flex-1 min-w-0">
        <Link
          to={`/tasks/${task.id}`}
          className="font-medium text-surface-800 hover:text-brand-600 transition-colors truncate block"
        >
          {task.title}
        </Link>
        {task.description && (
          <p className="text-sm text-surface-500 truncate mt-0.5">{task.description}</p>
        )}
      </div>

      {/* Priority */}
      {priority && (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${priority.color}`}>
          {priority.label}
        </span>
      )}

      {/* Due Date */}
      {task.due_date && (
        <div className={`flex items-center gap-1 text-sm ${isOverdue ? 'text-red-600' : 'text-surface-500'}`}>
          <HiCalendar className="w-4 h-4" />
          <span>{format(new Date(task.due_date), 'MMM d')}</span>
        </div>
      )}

      {/* Assignee */}
      {task.assignee ? (
        <div className="flex items-center gap-1" title={task.assignee.display_name}>
          <div className="w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 flex items-center justify-center text-xs font-medium">
            {task.assignee.display_name?.charAt(0)?.toUpperCase() || '?'}
          </div>
        </div>
      ) : (
        <HiUserCircle className="w-6 h-6 text-surface-300" />
      )}
    </div>
  )
}
